import { logger, sanitizeError } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  GET_PATIENT_PROFILES,
  LINK_SESSIONS_TO_PATIENT_PROFILE,
  type PatientProfile,
} from '../../contracts';
import { linkSessionsToPatientProfile } from '../api/patients';
import { invalidatePatientRecap } from '../query/useQueryPatientRecap';

export type LinkOwnSessionsToPatientProfileArgs<TTracking = unknown> = {
  patientProfile: PatientProfile;
  sessionIds: string[];
  /** Passed as a query param for session direct-share permission check.
   *  When a session is directly shared, the sharee can process (create/update/link/unlink)
   *  patient profiles on that session by providing its ID here. */
  sessionId?: string;
  /**
   * Suppress the success toast for this call. Used by composite operations
   * (e.g. create-and-link) that own their own user-facing toast — avoids
   * showing the user two stacked toasts for one logical action.
   *
   * OPAQUE TO THIS PACKAGE: it is never read here, only round-tripped to the
   * `onLinked` port, because a toast is an app reaction. It stays part of the
   * mutation variables so composite callers keep their existing call shape.
   */
  suppressToast?: boolean;
  /**
   * Mark the patient recap invalid without refetching it now. Only for callers
   * that navigate away as this resolves — create-and-link pushes straight to a
   * note-less session, which the backend skips in its recency walk, so an
   * immediate refetch just pulls the same cached blurb back onto a surface that
   * is already unmounting. The next mount still refetches.
   *
   * Off by default: every other caller (find-or-link, bulk link, EHR launch,
   * GDT) can link a session that already carries a finalised note while a
   * recap surface stays mounted, and those need the refetch to land.
   */
  deferRecapRefetch?: boolean;
  /**
   * Optional analytics metadata forwarded verbatim to `onLinked`. Carries
   * `surface`, `match_method`, `result_position`, `result_count_seen`,
   * `time_to_outcome_ms`, `search_id`, and `feature_flag_variant` for the
   * find-or-link combobox funnel; legacy callers omit it. Generic for the same
   * reason as the create hook's: the typed event shape stays app-side.
   */
  tracking?: TTracking;
};

/**
 * The FIRST-PERSON half of `useMutateLinkSessionsToPatientProfile`
 * (D2.2, ARCFE-1053).
 *
 * `POST /patient-profiles/{id}/sessions` is patients'; `POST /teams/{teamId}/
 * members/{memberId}/patient-profiles/{id}/sessions` is workspace's and stayed
 * in the app hub with the review-mode branch.
 *
 * WHAT THIS HOOK OWNS: the request, the patient-profiles invalidation (the
 * server-counted `session_count` on the list/drawer moves when a session is
 * linked), and the patient-recap invalidation (linking changes which sessions
 * the recap summarises). Both are patients caches.
 *
 * WHAT STAYED APP-SIDE, per child design D2: every SESSION cache reaction —
 * the `onMutate` optimistic writes across the three session-list query
 * versions, the linked-sessions-by-profile invalidation, the sessions-list
 * invalidation, and the per-session `GET_SESSION_BY_ID` patch + refetch. Those
 * are Session's caches and app integration sequences them; a `patients ->
 * session` import is exactly what the design forbids.
 *
 * SIDE-EFFECT ORDER: legacy interleaved one Session invalidation
 * (linked-sessions-by-profile) between the log line and the two invalidations
 * above. It now runs inside `onLinked` with the rest of the Session reactions,
 * in their original order relative to each other. Every key involved is
 * Session's and disjoint from `GET_PATIENT_PROFILES` / `GET_PATIENT_RECAP`, so
 * nothing observes the move.
 */
export function useMutateLinkOwnSessionsToPatientProfile<TTracking = unknown>({
  isViewingOtherSession = false,
  onOptimisticLink,
  onLinked,
  onLinkFailed,
}: {
  /** `isViewingOtherSessionAtom`, app-resolved (cohort-C). */
  isViewingOtherSession?: boolean;
  /**
   * Fires in `onMutate`, where the legacy optimistic session-list writes ran.
   * A separate port rather than part of `onLinked` because it is a different
   * mutation lifecycle position, not a reordering.
   */
  onOptimisticLink?: (input: LinkOwnSessionsToPatientProfileArgs<TTracking>) => void;
  onLinked?: (input: LinkOwnSessionsToPatientProfileArgs<TTracking>) => void;
  onLinkFailed?: (error: unknown) => void;
} = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [LINK_SESSIONS_TO_PATIENT_PROFILE],
    mutationFn: async (input: LinkOwnSessionsToPatientProfileArgs<TTracking>) => {
      if (input.patientProfile.id) {
        return await linkSessionsToPatientProfile(
          input.patientProfile.id,
          input.sessionIds,
          isViewingOtherSession ? input.sessionId : undefined
        );
      }
    },
    onMutate: (input) => {
      onOptimisticLink?.(input);
    },
    onSuccess: (_, input) => {
      logger.info('[PatientLink] Session linked to patient successfully');
      // session_count on the patient list / drawer is derived from a
      // server-side count of non-deleted sessions linked to the profile;
      // linking a session bumps that count, so the patient profiles cache
      // must refetch or the FE keeps showing the stale pre-link value.
      void queryClient.invalidateQueries({
        queryKey: [GET_PATIENT_PROFILES],
      });
      // Linking changes which sessions belong to the profile, so the recap
      // (popover + drawer) must be refetched or both keep showing the
      // pre-link summary. See `deferRecapRefetch` for why create-and-link opts
      // out of the immediate refetch.
      invalidatePatientRecap(
        queryClient,
        input.patientProfile.id,
        input.deferRecapRefetch ? 'none' : 'active'
      );
      onLinked?.(input);
    },
    onError: (error) => {
      logger.error(
        '[PatientLink] Failed to link session to patient',
        sanitizeError(error)
      );
      onLinkFailed?.(error);
    },
  });
}
